import { Component, inject, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BuildingService } from '@http/clients/building.service';
import { Building } from '@models/Building';
import { TabNames } from '@models/enums/TabNames';
import { AwaiterService } from '@services/awaiter.service';
import { ModalConfirmComponent } from '@shared/modals/modal-confirm/modal-confirm.component';
import { SectionHeaderComponent } from '@shared/section-header/section-header.component';
import { ModalService, SnackbarNotificationService } from 'ngx-lib';
import { Subscription } from 'rxjs';
import { BuildingsSingleOverviewComponent } from '../buildings-single/buildings-single-overview/buildings-single-overview.component';

@Component({
    selector: 'app-buildings-edit',
    templateUrl: './buildings-edit.component.html',
    styleUrls: ['./buildings-edit.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [SectionHeaderComponent, BuildingsSingleOverviewComponent]
})
export class BuildingsEditComponent implements OnInit {
    @ViewChild('overview')
    overview?: BuildingsSingleOverviewComponent;

    building: Building;

    buildingId: number;

    buttonLabel?: string;

    continueButtonLabel?: string;

    title?: string;

    entitySubscription?: Subscription;

    private readonly awaiter = inject(AwaiterService);
    private readonly buildingService = inject(BuildingService);
    private readonly snackbarNotificationService = inject(SnackbarNotificationService);
    private readonly modalService = inject(ModalService);
    private readonly route = inject(ActivatedRoute);
    private readonly router = inject(Router);

    constructor() {
        this.building = new Building();
        this.buildingId = parseInt(this.route.snapshot.paramMap.get('id') ?? '0');
        this.buttonLabel = 'Create';
        this.continueButtonLabel = 'Continue Creation';
        this.title = 'Create New Building';
    }

    async ngOnInit(): Promise<void> {
        if (this.buildingId) {
            this.buttonLabel = 'Save';
            this.continueButtonLabel = 'Continue Edition';
            this.title = 'Edit Building';

            this.entitySubscription = this.route.data.subscribe(async ({ entityData }) => {
                this.building = Building.fromInterface(entityData);
            });
        }
    }

    async saveBuilding(): Promise<void> {
        if (!this.overview?.validate()) return;

        await this.awaiter.awaitAction('Saving Building', async () => {
            await this.buildingService.save(this.building);
            this.snackbarNotificationService.success('Building saved successfully.');
            this.router.navigate(
                this.buildingId
                    ? [`/buildings-single/${this.buildingId}/${TabNames[TabNames.Overview]}`]
                    : ['/buildings']
            );
        });
    }

    async cancel(): Promise<void> {
        const responseOk = await this.modalService.open(ModalConfirmComponent, {
            acceptCaption: 'Cancel Editing',
            cancelCaption: this.continueButtonLabel,
            content: 'Are you sure you want to exit? Your changes will be lost',
            title: 'Cancel Save Building'
        });

        if (responseOk) {
            this.router.navigate(
                this.buildingId
                    ? [`/buildings-single/${this.buildingId}/${TabNames[TabNames.Overview]}`]
                    : ['/buildings']
            );
        }
    }
}
